import BackgroundGradient from "%/dynamic-components/render-engine/options/background-options/background-gradient";
import { hasUnfilledPlaceholder } from "%/dynamic-component-editor/standard-elements/click-actions/resolve-context-href";
import { ContextHrefMixin } from "%/dynamic-component-editor/standard-elements/click-actions/context-href-mixin";
import BackgroundColor from "%/dynamic-components/render-engine/options/background-options/background-color";
import BoxShadow from "%/dynamic-components/render-engine/options/box-shadow-options/box-shadow";
import ClickAction from "%/dynamic-components/render-engine/options/click-actions/click-action";
import { useGeneralStore } from "%/stores/general-store";
import { navigateBack } from "%/dynamic-components/render-engine/helpers/navigate-back";
import DynamicComponentError from "%/errors/dynamic-component-error";
import Padding, {
  PADDING_DEFAULT_EMPTY_VALUE,
} from "%/dynamic-components/render-engine/options/spacing-options/padding";
import BaseOption from "%/dynamic-components/render-engine/classes/base-option";
import Margins, {
  MARGIN_DEFAULT_EMPTY_VALUE,
} from "%/dynamic-components/render-engine/options/spacing-options/margins";
import ButtonType from "%/dynamic-components/render-engine/options/button-options/button-type";
import Icon from "%/dynamic-components/render-engine/options/icon-options/icon";
import IconPosition from "%/dynamic-components/render-engine/options/icon-options/icon-position";
import LayoutWidth from "%/dynamic-components/render-engine/options/layout-options/layout-width";
import LayoutHeight from "%/dynamic-components/render-engine/options/layout-options/layout-height";
import ButtonSize from "%/dynamic-components/render-engine/options/button-options/button-size";
import BorderRadius from "%/dynamic-components/render-engine/options/border-options/border-radius";
import LinkType from "%/dynamic-components/render-engine/options/link-type";
import { resolveContextualPageValue } from "%/dynamic-components/render-engine/options/click-actions/contextual-page-value";
import ButtonBranding from "%/dynamic-components/render-engine/classes/branding/button-branding";
import ButtonBorderColor from "%/dynamic-components/render-engine/options/button-options/button-border-color";
import ButtonBorderWidth from "%/dynamic-components/render-engine/options/button-options/button-border-width";
import IconGap from "%/dynamic-components/render-engine/options/button-options/icon-gap";
import ButtonIconSize from "%/dynamic-components/render-engine/options/button-options/button-icon-size";
import TextAlignment from "%/dynamic-components/render-engine/options/button-options/text-alignment";
import { GestureActionsMixin } from "%/dynamic-component-editor/standard-elements/click-actions/gesture-actions-mixin";

// classes-v2 (unlike DCE) never calls an option's own convertConfigValueToCompiledValue —
// "auto" passes through unconverted, so it must be normalized to null here instead.
function nullIfAuto(value) {
  return value === "auto" ? null : value;
}

export const SharedButtonComputedStylesMixin = {
  mixins: [ContextHrefMixin, GestureActionsMixin],
  brandingType: ButtonBranding,
  computed: {
    buttonVariant() {
      return this.getOptionValue(ButtonType) || "primary";
    },
    defaultTemplateName() {
      return this.hasTextContent || this.editable ? "default" : "hidden";
    },
    displayLeftIcon() {
      return this.getOptionValue(Icon) && this.getOptionValue(IconPosition) === "beforeText";
    },
    displayRightIcon() {
      return this.getOptionValue(Icon) && (this.getOptionValue(IconPosition) === "afterText" || this.getOptionValue(IconPosition) === "noText");
    },
    displayTopIcon() {
      return this.getOptionValue(Icon) && this.getOptionValue(IconPosition) === "aboveText";
    },
    displayBottomIcon() {
      return this.getOptionValue(Icon) && this.getOptionValue(IconPosition) === "belowText";
    },
    actionType() {
      return this.getOptionValue(ClickAction)?.action;
    },
    myIcon() {
      return this.getOptionValue(Icon);
    },
    myWidth() {
      return this.getOptionValue(LayoutWidth);
    },
    myHeight() {
      return this.getOptionValue(LayoutHeight);
    },
    myButtonSize() {
      return this.getOptionValue(ButtonSize) || "standard";
    },
    shouldRenderButton() {
      return true;
    },
    hasPaddingSet() {
      return Object.values(this.myPadding()).every(value => value !== "0px");
    },
    // Legacy publications never registered these options, so the resolver returns
    // `undefined` rather than the declared default — coalesce or every button crashes.
    myIconGap() {
      return nullIfAuto(this.getOptionValue(IconGap)) ?? null;
    },
    myIconSize() {
      return nullIfAuto(this.getOptionValue(ButtonIconSize));
    },
    // "hug" once the effective (possibly inherited-from-another-breakpoint) gap/alignment
    // is non-default, so untouched buttons don't move but inherited breakpoints still group.
    myContentWidth() {
      const alignment = this.getOptionValue(TextAlignment) ?? "center";
      return this.myIconGap !== null || alignment !== "center" ? "hug" : "fill";
    },
    buttonStyleOverrides() {
      const margins = this.myMargins();
      let overrides = {
        borderRadius: this.getOptionValue(BorderRadius),
        marginTop: margins.top || "none",
        marginBottom: margins.bottom || "none",
        marginLeft: margins.left || "none",
        marginRight: margins.right || "none",
        boxShadow: this.getOptionValue(BoxShadow),
        boxShadowHover: this.getOptionValue(BoxShadow, BaseOption.$states.HOVER),
        [this.getButtonOverrideKeyForVariant("BackgroundGradient")]: this.getOptionValue(BackgroundGradient),
        [this.getButtonOverrideKeyForVariant("HoverBackgroundGradient")]: this.getOptionValue(BackgroundGradient, BaseOption.$states.HOVER),
        [this.getButtonOverrideKeyForVariant("BorderColor")]: this.getOptionValue(ButtonBorderColor) || "none",
        [this.getButtonOverrideKeyForVariant("BorderWidth")]: this.getOptionValue(ButtonBorderWidth) || "none",
        [this.getButtonOverrideKeyForVariant("HoverBorderColor")]: this.getOptionValue(ButtonBorderColor, BaseOption.$states.HOVER) || "none",
      };

      if (this.hasPaddingSet) {
        const padding = this.myPadding();
        overrides = {
          ...overrides,
          [`${this.myButtonSize}PaddingTop`]: padding.top || "none",
          [`${this.myButtonSize}PaddingRight`]: padding.right || "none",
          [`${this.myButtonSize}PaddingBottom`]: padding.bottom || "none",
          [`${this.myButtonSize}PaddingLeft`]: padding.left || "none",
        };
      }

      const backgroundValue = this.getOptionValue(BackgroundColor);
      const backgroundHoverValue = this.getOptionValue(BackgroundColor, BaseOption.$states.HOVER);

      if (backgroundValue !== "none") {
        overrides[this.getButtonOverrideKeyForVariant("BackgroundColor")] = backgroundValue;
      }

      if (backgroundHoverValue !== "none") {
        overrides[this.getButtonOverrideKeyForVariant("HoverBackgroundColor")] = backgroundHoverValue;
      }

      if (this.myIconGap !== null) {
        overrides.contentGap = this.myIconGap;
      }

      return overrides;
    },
    clickValue() {
      let urlValue = this.getOptionValue(ClickAction)?.value;

      if (!this.linkType) {
        return;
      }

      switch (this.linkType) {
        case LinkType.LINK_TYPE_HYPERLINK:
          return urlValue;
        case LinkType.LINK_TYPE_PAGE: {
          const contextHref = this.contextResolvedHref(
            this.getOptionValue(ClickAction), urlValue, this.links.web,
          );
          if (contextHref) {
            return contextHref;
          }
          urlValue = resolveContextualPageValue(urlValue);
          if (urlValue === null || urlValue === undefined) {
            return;
          }
          if (urlValue.startsWith("/")) {
            urlValue = urlValue.substring(1);
          }
          const pageHref = `${this.links.web}/${urlValue}`;
          return hasUnfilledPlaceholder(pageHref) ? undefined : pageHref;
        }
        case LinkType.LINK_TYPE_STORY:
          return `${this.links.web}/story/${urlValue}`;
        case LinkType.LINK_TYPE_HASHTAG:
          return `${this.links.web}/stories/${urlValue}`;
        default:
          throw new DynamicComponentError("Url type not found", {
            linkType: this.linkType,
            urlValue,
          });
      }
    },
    linkType() {
      return this.getOptionValue(ClickAction)?.linkType;
    },
    openInNewTab() {
      return this.getOptionValue(ClickAction)?.openInNewTab;
    },
  },
  methods: {
    setDefaultBrandingValue() {
      // override text element mixin method to avoid adding text branding store
    },
    myPadding(state = BaseOption.$states.DEFAULT) {
      let paddingValues = this.getOptionValue(Padding, state, null) ?? {};
      const paddingKeys = Object.keys(paddingValues);
      paddingKeys.forEach(paddingKey => {
        const paddingVal = paddingValues[paddingKey];
        if (paddingVal === null) {
          paddingValues[paddingKey] = PADDING_DEFAULT_EMPTY_VALUE;
        }
      });
      return paddingValues;
    },
    myMargins(state = BaseOption.$states.DEFAULT) {
      let marginValues = this.getOptionValue(Margins, state, null) ?? {};
      const marginKeys = Object.keys(marginValues);
      marginKeys.forEach(marginKey => {
        const marginVal = marginValues[marginKey];
        if (marginVal === null) {
          marginValues[marginKey] = MARGIN_DEFAULT_EMPTY_VALUE;
        }
      });
      return marginValues;
    },
    getButtonOverrideKeyForVariant(overrideKey) {
      return `${this.buttonVariant}Variant${overrideKey}`;
    },
    resolveClickActionHref(value) {
      if (!value || /^[a-z][a-z0-9+.-]*:/i.test(value) || value.startsWith("//")) {
        return value;
      }
      if (!value.startsWith("/")) {
        return value;
      }
      const webBase = useGeneralStore().links.web || "";
      return `${webBase}${value}`;
    },
    handleButtonClick(e) {
      // A click that is authored as a gesture belongs to the gesture engine,
      // which runs the same action: doing it here as well navigates twice, or
      // opens two tabs.
      if (this.gestureOwnsTheClick) {
        return;
      }
      if (this.handleCustomClick) {
        this.handleCustomClick(e);
        return;
      }

      if (this.actionType === "goBack") {
        navigateBack(useGeneralStore().links.web);
        return;
      }

      if (this.actionType === "goToUrl") {
        if (!this.clickValue) {
          return;
        }

        const a = document.createElement("a");
        if (this.openInNewTab) {
          a.target = "_blank";
        }
        a.href = this.resolveClickActionHref(this.clickValue);
        a.click();
      } else {
        // ignore for now
      }
    },
  },
};
